"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Procedure = void 0;
const internal_1 = require("../internal");
const types_1 = require("../types");
const conversion_1 = require("../utils/conversion");
const internal_2 = require("../utils/internal");
/**
 * @hidden
 */
function assertOnlyOneAsset(assets) {
    if (assets.length > 1) {
        throw new internal_1.PolymeshError({
            code: types_1.ErrorCode.FatalError,
            message: 'Procedures cannot require permissions for more than one Asset. Please contact the Polymesh team',
        });
    }
}
/**
 * @hidden
 */
function getAgentPermissionsResult(identity, asset, transactions) {
    return __awaiter(this, void 0, void 0, function* () {
        return identity
            ? identity.assetPermissions.checkPermissions({
                asset,
                transactions,
            })
            : { result: false, missingPermissions: transactions };
    });
}
/**
 * @hidden
 *
 * Represents an operation performed on the Polymesh blockchain.
 * A Procedure can be prepared to return a promise that resolves
 * to a {@link PolymeshTransaction} (or {@link PolymeshTransactionBatch}) that can be run
 */
class Procedure {
    /**
     * @hidden
     *
     * @param prepareTransactions - function that prepares the transaction queue
     * @param getAuthorization - can be a ProcedureAuthorization object or a function that returns a ProcedureAuthorization object
     */
    constructor(prepareTransactions, getAuthorization = () => __awaiter(this, void 0, void 0, function* () { return ({}); }), prepareStorage = () => __awaiter(this, void 0, void 0, function* () { return ({}); })) {
        this._storage = null;
        this._context = null;
        this._signerMultiSig = null;
        this.prepareTransactions = prepareTransactions;
        if (typeof getAuthorization !== 'function') {
            this.getAuthorization = () => getAuthorization;
        }
        else {
            this.getAuthorization = getAuthorization;
        }
        this.prepareStorage = prepareStorage;
    }
    /**
     * @hidden
     * Set the context and storage (if not already set), return the Context
     */
    setup(args, context, opts = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this._context) {
                const ctx = context.clone();
                const { signingAccount, nonce } = opts;
                if (typeof nonce === 'function') {
                    ctx.setNonce(yield nonce());
                }
                else {
                    ctx.setNonce(yield nonce);
                }
                let multiSig;
                if (signingAccount) {
                    const account = (0, internal_2.asAccount)(signingAccount, context);
                    [multiSig] = yield Promise.all([
                        account.getMultiSig(),
                        ctx.setSigningAddress((0, conversion_1.signerToString)(signingAccount)),
                    ]);
                }
                else {
                    multiSig = yield ctx.getSigningAccount().getMultiSig();
                }
                this._signerMultiSig = multiSig !== null && multiSig !== void 0 ? multiSig : null;
                this._context = ctx;
            }
            if (!this._storage) {
                this._storage = yield this.prepareStorage(args);
            }
            return this._context;
        });
    }
    /**
     * @hidden
     * Reset the procedure
     */
    cleanup() {
        this._context = null;
        this._storage = null;
    }
    /**
     * @hidden
     */
    _checkAuthorization(args, context, opts) {
        return __awaiter(this, void 0, void 0, function* () {
            const ctx = yield this.setup(args, context, opts);
            const checkAuthorizationResult = yield this.getAuthorization(args);
            const { permissions = true, roles = true } = checkAuthorizationResult;
            const { signerPermissions = permissions, agentPermissions = permissions } = checkAuthorizationResult;
            let identity = null;
            let rolesResult;
            let noIdentity = false;
            const account = this._signerMultiSig || ctx.getSigningAccount();
            const fetchIdentity = () => __awaiter(this, void 0, void 0, function* () { return identity || account.getIdentity(); });
            if (typeof roles === 'boolean') {
                rolesResult = { result: roles };
            }
            else if (typeof roles === 'string') {
                rolesResult = { result: false, message: roles };
            }
            else {
                identity = yield fetchIdentity();
                noIdentity = !identity;
                rolesResult = { result: false, missingRoles: roles };
                if (identity) {
                    rolesResult = yield identity.checkRoles(roles);
                }
            }
            let agentPermissionsResult;
            let signerPermissionsAwaitable;
            const accountFrozenPromise = account.isFrozen();
            if (typeof signerPermissions === 'boolean') {
                signerPermissionsAwaitable = { result: signerPermissions };
            }
            else if (typeof signerPermissions === 'string') {
                signerPermissionsAwaitable = { result: false, message: signerPermissions };
            }
            else {
                signerPermissionsAwaitable = account.checkPermissions(signerPermissions);
            }
            if (typeof agentPermissions === 'boolean') {
                agentPermissionsResult = { result: agentPermissions };
            }
            else if (typeof agentPermissions === 'string') {
                agentPermissionsResult = { result: false, message: agentPermissions };
            }
            else {
                const { assets, transactions } = agentPermissions;
                agentPermissionsResult = { result: true };
                if ((assets === null || assets === void 0 ? void 0 : assets.length) && (transactions === null || transactions === void 0 ? void 0 : transactions.length)) {
                    assertOnlyOneAsset(assets);
                    identity = yield fetchIdentity();
                    noIdentity = !identity;
                    agentPermissionsResult = yield getAgentPermissionsResult(identity, assets[0], transactions);
                }
            }
            const hasSignerPermissions = yield signerPermissionsAwaitable;
            const accountFrozen = yield accountFrozenPromise;
            return {
                roles: rolesResult,
                signerPermissions: hasSignerPermissions,
                agentPermissions: agentPermissionsResult,
                accountFrozen,
                noIdentity,
            };
        });
    }
    /**
     * Check if the current user has sufficient authorization to run the procedure
     *
     * @param args - procedure arguments
     */
    checkAuthorization(args, context, opts) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                return yield this._checkAuthorization(args, context, opts);
            }
            finally {
                this.cleanup();
            }
        });
    }
    /**
     * Build a {@link PolymeshTransaction} or {@link PolymeshTransactionBatch} that can be run
     *
     * @param args.args - arguments required to prepare the queue
     * @param args.transformer - optional function that transforms the Procedure result
     * @param context - context in which the resulting queue will run
     * @param opts.signer - address that will be used as a signer for this procedure
     */
    prepare(args, context, opts) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const { args: procArgs, transformer } = args;
                const mortality = (opts === null || opts === void 0 ? void 0 : opts.mortality) || {
                    immortal: false,
                };
                const ctx = yield this.setup(procArgs, context, opts);
                // parallelize the async calls
                const prepareTransactionsPromise = (0, internal_2.defusePromise)(this.prepareTransactions(procArgs));
                const { roles, signerPermissions, agentPermissions, accountFrozen, noIdentity } = yield this._checkAuthorization(procArgs, ctx);
                if (noIdentity) {
                    throw new internal_1.PolymeshError({
                        code: types_1.ErrorCode.NotAuthorized,
                        message: 'This procedure requires the signing Account to have an associated Identity',
                    });
                }
                if (accountFrozen) {
                    throw new internal_1.PolymeshError({
                        code: types_1.ErrorCode.NotAuthorized,
                        message: "The signing Account can't execute this procedure because it is frozen",
                    });
                }
                if (!signerPermissions.result) {
                    const { message, missingPermissions } = signerPermissions;
                    throw new internal_1.PolymeshError({
                        code: types_1.ErrorCode.NotAuthorized,
                        message: "The signing Account doesn't have the required permissions to execute this procedure",
                        data: {
                            message,
                            missingPermissions,
                        },
                    });
                }
                if (!agentPermissions.result) {
                    const { message, missingPermissions } = agentPermissions;
                    throw new internal_1.PolymeshError({
                        code: types_1.ErrorCode.NotAuthorized,
                        message: "The signing Identity doesn't have the required permissions to execute this procedure",
                        data: {
                            message,
                            missingPermissions,
                        },
                    });
                }
                if (!roles.result) {
                    const { message, missingRoles } = roles;
                    throw new internal_1.PolymeshError({
                        code: types_1.ErrorCode.NotAuthorized,
                        message: "The signing Identity doesn't have the required roles to execute this procedure",
                        data: {
                            message,
                            missingRoles,
                        },
                    });
                }
                const procedureResult = yield prepareTransactionsPromise;
                const signingAddress = ctx.getSigningAddress();
                const signer = ctx.getExternalSigner();
                const spec = Object.assign(Object.assign({}, procedureResult), { signer,
                    signingAddress,
                    mortality,
                    transformer, multiSig: (_a = this._signerMultiSig) !== null && _a !== void 0 ? _a : undefined });
                if (!('transactions' in spec)) {
                    return new internal_1.PolymeshTransaction(spec, ctx);
                }
                else if (spec.transactions.length === 1) {
                    const [{ transaction, args: txArgs, fee, feeMultiplier }] = spec.transactions;
                    return new internal_1.PolymeshTransaction(Object.assign(Object.assign({}, spec), { transaction,
                        fee,
                        feeMultiplier, args: txArgs }), ctx);
                }
                else {
                    return new internal_1.PolymeshTransactionBatch(spec, ctx);
                }
            }
            finally {
                this.cleanup();
            }
        });
    }
    /**
     * internal data container. Used to store common fetched/processed data that is
     *   used by both `prepareTransactions` and `checkAuthorization`
     */
    get storage() {
        const { _storage: storage } = this;
        if (!storage) {
            throw new internal_1.PolymeshError({
                code: types_1.ErrorCode.FatalError,
                message: 'Attempt to access storage before it was set',
            });
        }
        return storage;
    }
    /**
     * contains the data services, signing Account, etc. In short, the *context* in which
     *   the Procedure is being run
     */
    get context() {
        const { _context: context } = this;
        if (!context) {
            throw new internal_1.PolymeshError({
                code: types_1.ErrorCode.FatalError,
                message: 'Attempt to access context before it was set',
            });
        }
        return context;
    }
    /**
     * Get the authorization data (permissions) required to run this procedure
     *
     * @param args - procedure arguments
     * @returns authorization data containing required roles and permissions
     */
    requiredAuthorizations(args) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.getAuthorization(args);
        });
    }
}
exports.Procedure = Procedure;
//# sourceMappingURL=Procedure.js.map